import { toastr } from 'react-redux-toastr';
import ReactGA from 'react-ga';
import { AuthTypes, defaultAuthPayload, IRefreshToken } from "./types";
import storageManager from '../../../share/utils/localStorage';
import { AUTH_STORAGE_KEY } from '../../../share/utils/apiAuthToken';
import { AgenciesService } from '../../../share/services/agencies';
import routes from '../../Routing/routes';
import { NotificationsService } from 'src/share/services/notifications';
import { AuditService } from 'src/share/services/audit';
import { TermsOfUseService } from 'src/share/services/termsOfUse';
import { TermsOfUseUserStatus } from 'src/share/types/termsOfUse';
import { getUserType } from '../helpers/getUserType';
import { authModalState } from '../AuthModal/useAuthModal';
import { notifyApiError, suppressApiErrorsFor } from '../../../share/utils/notifyApiError';
import { cognitoOAuth } from '../../../share/services/cognito-oauth';
import environmentHelper from '../../../share/utils/environmentHelper';

// Shared post-authentication tail: runs once a full Cognito session exists
// (whether from a plain login or after completing an MFA challenge).
const runPostLogin = async (dispatch: any, username: string, session: any) => {
    storageManager.save(AUTH_STORAGE_KEY, { idToken: session.getIdToken() });
    const authData = session.getIdToken().decodePayload();
    const agencyCode = authData['custom:agencyCode'];
    let lastLogin = null;
    try {
        lastLogin = await AuditService.logLogin(username);
    } catch (e) {
        // tslint:disable-next-line:no-console
        console.warn('[runPostLogin] audit log failed (non-fatal):', e);
    }
    const agencies = await AgenciesService.get();
    const notifications = await NotificationsService.get();
    const userType = getUserType(authData);
    const termsOfUseStatus = await TermsOfUseService.getUserAgreementStatus(username);
    const payload = {
        username,
        idToken: session.getIdToken(),
        accessToken: session.getAccessToken(),
        refreshToken: session.getRefreshToken(),
        errorMessage: '',
        loading: false,
        userType,
        agencies,
        agencyCode,
        notifications,
        lastLogin,
        termsOfUseStatus
    };
    storageManager.save(AUTH_STORAGE_KEY, payload);
    ReactGA.event({
        category: 'Login',
        action: 'User login',
        label: 'Login management'
    });
    dispatch({
        type: AuthTypes.LogIn,
        payload
    });
    // A fresh session is now established — clear any stale "session expired" modal
    // state (e.g. set by stray 403s fired during the previous logout transition)
    // so it doesn't surface on this new session.
    authModalState.next({ isModalOpen: false, refresh: false });
};

const dispatchLoginFailed = (dispatch: any, message: string = 'Login Failed!') => {
    toastr.error('Login error', message);
    storageManager.clear();
    dispatch({
        type: AuthTypes.LogInFailed,
        payload: {
            ...defaultAuthPayload,
            errorMessage: message
        }
    });
};

export const loginActions = {
    logout: (history: any): any =>
        async (dispatch: any) => {
            // Detect an OAuth/hosted-UI session before clearing storage, so we can
            // also end the Cognito SSO session — otherwise the hosted-UI cookie
            // lingers and "Sign in with Cognito" re-authenticates silently.
            const stored = storageManager.get(AUTH_STORAGE_KEY);
            const aud = stored && stored.idToken && stored.idToken.payload && stored.idToken.payload.aud;
            const oauthClientId = environmentHelper.getCognitoOAuthConfig().clientId;
            const isOAuthSession = !!oauthClientId && aud === oauthClientId;

            storageManager.clear();
            // Suppress error toasts from stray in-flight requests that fail across
            // the logout transition (their catch blocks can run before/after the
            // storage clear).
            suppressApiErrorsFor(15000);
            // Reset the global session-expired modal state so a stale 403-triggered
            // modal from this session doesn't reappear on the next login (the SPA
            // doesn't reload between logout and login).
            authModalState.next({ isModalOpen: false, refresh: false });
            ReactGA.event({
                category: 'Logout',
                action: 'User logout',
                label: 'Login management'
            });
            dispatch({
                type: AuthTypes.LogOut
            });
            if (isOAuthSession) {
                // Full-page redirect to Cognito /logout; it clears the hosted-UI
                // cookie and returns to /login (a registered logout URL).
                window.location.assign(cognitoOAuth.buildLogoutUrl());
            } else {
                history.replace(routes.LOGIN);
            }
        },
    // Finish a login from the managed-login (hosted UI) callback: OAuthCallback has
    // already exchanged the code for a full session; just run the post-login tail.
    completeLogin: (username: string, session: any): any =>
        async (dispatch: any) => {
            dispatch({
                type: AuthTypes.LogInLoading,
                payload: {
                    ...defaultAuthPayload,
                    username,
                    loading: true,
                }
            })
            try {
                await runPostLogin(dispatch, username, session);
            } catch (error) {
                dispatchLoginFailed(dispatch);
            }
        },
    refreshLogIn: (refreshToken: IRefreshToken | null): any =>
        async (dispatch: any) => {
            if (!refreshToken) { return; }
            try {
                const tokens = await cognitoOAuth.refreshTokens(refreshToken.token);
                const session = cognitoOAuth.buildSessionAdapter(tokens);
                const payload = {
                    idToken: session.getIdToken(),
                    accessToken: session.getAccessToken(),
                    refreshToken: session.getRefreshToken(),
                    errorMessage: '',
                    loading: false
                };
                storageManager.save(AUTH_STORAGE_KEY, { ...storageManager.get(AUTH_STORAGE_KEY), ...payload });
                ReactGA.event({
                    category: 'Login',
                    action: 'Refresh login',
                    label: 'Login management'
                });
                dispatch({
                    type: AuthTypes.RefreshLogin,
                    payload
                })
            } catch (error) {
                toastr.error('Login error', 'Login Failed!')
                dispatch({
                    type: AuthTypes.LogInFailed,
                    payload: {
                        ...defaultAuthPayload,
                        errorMessage: 'Login Failed!'
                    }
                })
            }
        },
    getAgencies: (): any => async (dispatch: any) => {
        try {
            const agencies = await AgenciesService.get()
            dispatch({
                type: AuthTypes.GetAgencies,
                payload: {
                    agencies
                }
            })
        } catch (error) {
            notifyApiError('Agencies', 'There was a problem loading the agencies')
        }
    },
    getNotifications: (): any => async (dispatch: any) => {
        try {
            const notifications = await NotificationsService.get();
            dispatch({
                type: AuthTypes.GetNotifications,
                payload: {
                    notifications
                }
            })
        } catch (error) {
            // 403 means the session expired — the auth modal already handles it.
            // Only toast for other failures (5xx, network errors) so users get
            // two simultaneous "something's wrong" messages when a token expires.
            if (!error || error.status !== 403) {
                notifyApiError('Notifications', 'There was a problem loading your notifications')
            }
        }
    },
    deleteNotifications: (ids: string[], type: string): any => async (dispatch: any) => {
        try {
            await NotificationsService.remove(ids);
            dispatch({
                type: AuthTypes.DeleteNotifications,
                payload: {
                    type
                }
            })
        } catch (error) {
            toastr.error('Notifications', 'There was a problem removing your notifications')
        }
    },
    setTermsOfUserStatus: (termsOfUseStatus: TermsOfUseUserStatus) => (dispatch: any) => {
        storageManager.save(AUTH_STORAGE_KEY, { ...storageManager.get(AUTH_STORAGE_KEY), termsOfUseStatus });
        dispatch({
            type: AuthTypes.TermsOfUseStatus,
            payload: {
                termsOfUseStatus
            }
        })
    }
}
